import {
  CircularProgress,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Phone } from '@material-ui/icons';
import { SuspendedPhoneText } from '@superdispatch/phones';
import { ColorDynamic, Stack } from '@superdispatch/ui';
import { Box, DescriptionItem } from '@superdispatch/ui-lab';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { TrailerIcon } from 'shared/icons/TrailerIcon';
import {
  splitTextByLine,
  StyledNotesIcon,
} from 'shared/modules/driver/DriverUtils';
import { FloatingButtonArea } from 'shared/ui/FloatingButtonArea';
import styled from 'styled-components';
import { UserDTO } from '../data/UsersDTO';

const StyledListItemText = styled(ListItemText)`
  word-break: break-all;

  & .MuiTypography-root {
    max-width: 200px;
  }
`;

const StyledBox = styled(Box)`
  vertical-align: middle;
`;

interface UserItemProps {
  user: UserDTO;
  onClick: () => void;
  isSubmitting: boolean;
  isAssigned?: boolean;
  selectedGUID: string | undefined;
}

export function UserItem({
  user,
  onClick,
  isSubmitting,
  isAssigned,
  selectedGUID,
}: UserItemProps) {
  const isSelected = user.driver?.guid === selectedGUID;
  const isLoading = isSelected && isSubmitting;
  const userLabel = user.internal_name || user.name || user.email || '';

  return (
    <ListItem
      button={true}
      aria-label="User"
      disabled={isSubmitting || isAssigned || isLoading}
      onClick={onClick}
    >
      <FloatingButtonArea
        buttonPlacement="right"
        buttonVisible={isLoading}
        button={
          <Typography variant="body1" color="primary" component="div">
            {isLoading ? <CircularProgress size="1em" /> : 'Assign'}
          </Typography>
        }
      >
        <StyledListItemText
          disableTypography={true}
          primary={
            <Stack>
              <Typography variant="h4">
                {userLabel}
                {!!user.internal_notes && (
                  <Tooltip
                    arrow={true}
                    placement="top"
                    title={splitTextByLine(user.internal_notes)}
                    interactive={true}
                  >
                    <StyledBox paddingLeft="xxsmall" display="inline">
                      <StyledNotesIcon htmlColor={ColorDynamic.Dark100} />
                    </StyledBox>
                  </Tooltip>
                )}
              </Typography>

              <Stack space={['small', 'xsmall']}>
                <DescriptionItem icon={<Phone />} fallback="N/A">
                  {!!user.phone_number && (
                    <SuspendedPhoneText
                      phone={user.phone_number}
                      fallback={user.phone_number}
                    />
                  )}
                </DescriptionItem>

                {user.driver?.trailer.capacity != null && (
                  <DescriptionItem icon={<TrailerIcon />}>
                    {user.driver.trailer.capacity}
                    {formatPlural(
                      user.driver.trailer.capacity,
                      ' vehicle trailer',
                      ' vehicles trailer',
                    )}
                  </DescriptionItem>
                )}
              </Stack>
            </Stack>
          }
        />
      </FloatingButtonArea>
    </ListItem>
  );
}
